/**
 * НИЖНИЕ ГРАНИЦЫ ЗАПИСИ ЗАДНИМ ЧИСЛОМ — Р-104 и Р-105, одна арифметика на
 * сервер и на веб.
 *
 * ═══ ПОЧЕМУ ОДИН МОДУЛЬ НА ДВА РЕШЕНИЯ ═══════════════════════════════════
 *
 * Решения приняты одним кругом 29.08.2026 и отвечают на один вопрос — «как
 * глубоко в прошлое можно писать», — но отвечают РАЗНО: час уходит на два
 * месяца назад (Р-104), задача себе — на неделю (Р-105). Расхождение
 * НАМЕРЕННОЕ и названо вслух в самом Р-105 («Стык с Р-104»): глубже недели
 * остаётся только короткая дорога, которая несёт накладное время, а задача
 * несёт работу, у которой есть план. Держать оба порога рядом — единственный
 * способ, чтобы следующий читатель увидел расхождение как решение, а не как
 * недосмотр, и не «привёл к единообразию».
 *
 * ═══ ⚠️ «СЕГОДНЯ» СЮДА ПРИХОДИТ ПАРАМЕТРОМ, И ЭТО НЕСУЩЕЕ ════════════════
 *
 * Функции ЧИСТЫЕ: `new Date()` здесь не вызывается ни разу. Оба решения
 * записывают правило дословно — «сегодня» считается ОДНИМ каноническим
 * способом, `queryTodayMsk` (`apps/api/src/common/date/today-msk.ts`), то есть
 * Postgres в `Europe/Moscow`. Сравнение с UTC-датой приложения ЗАПРЕЩЕНО:
 * проект живёт в Москве, и с 00:00 до 02:59 МСК UTC-дата на сутки позади —
 * законная запись получила бы отказ, а воспроизводился бы он только ночью.
 * Ровно этот стык уже дал в Ф5 трёхмесячный дефект (Р-99).
 *
 * Клиент подставляет сюда ту же серверную дату (`today_msk` сетки загрузки,
 * `today` списка работ), а не `new Date()` браузера: иначе у сотрудника с
 * чужим часовым поясом на ноутбуке форма и сервер разошлись бы, и разошлись
 * бы только у него.
 *
 * ═══ ⚠️ ГРАНИЦА НА ЗАПИСЬ, А НЕ НА ЧТЕНИЕ ════════════════════════════════
 *
 * Модуль отвечает на вопрос «можно ли ЗАВЕСТИ строку этим днём». Исторические
 * строки, лежащие глубже границы, законны навсегда, читаются как прежде, и
 * правка их часов границей не спрашивается (Р-104, четвёртая граница).
 *
 * ⚠️ АРИФМЕТИКА СТРОКОВАЯ. `YYYY-MM-DD` лексикографически совпадает с
 * хронологическим порядком, поэтому сравнение — обычное `>=`, и живёт оно
 * РОВНО В ОДНОМ месте: `isIsoDateAtOrAfterFloor` ниже. Единственная точка, где
 * `Date` допустим, — сложение суток в `addDaysIso`: там и вход, и выход
 * остаются календарной датой без времени.
 *
 * 🚫 ⚠️ БЫЛО «сравнение — обычное `<`» (волна Ф5 · В9.9). Формулировка не
 * врала про арифметику, но описывала сравнение как ОБЫЧАЙ, а не как место, —
 * и под неё веб честно завёл два своих `<`. Снято фикс-паком круга ревью
 * 30.08.2026; разбор — в докблоке `isIsoDateAtOrAfterFloor`.
 */
import { addDaysIso } from '../work';

/** `YYYY-MM-DD` → `DD.MM.YYYY`. Разбора в `Date` не делает — часовой пояс не втащить. */
function humanIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * ЕДИНСТВЕННОЕ СРАВНЕНИЕ ДАТЫ С ГРАНИЦЕЙ НА ВЕСЬ ПРОЕКТ — сервер и веб.
 *
 * ⚠️ ЗАВЕДЕНА ФИКС-ПАКОМ КРУГА РЕВЮ 30.08.2026, И ЗАВЕДЕНА ПО НАСТОЯЩЕЙ
 * НАХОДКЕ, а не для красоты. Волна Ф5 · В9.9 заявила «одна арифметика на
 * сервер и на веб»; проверяющий показал мутацией, что заявление верно только
 * НАПОЛОВИНУ. Общим было ВЫЧИСЛЕНИЕ границы (`factBackdateFloorIso`,
 * `selfTaskStartFloorIso`), а СРАВНЕНИЕ существовало в ТРЁХ копиях: предикаты
 * этого модуля плюс два рукописных `<` в вебе —
 * `workload-cell-modal.tsx` (резолвер даты факта) и `task-form.modal.tsx`
 * (нижний край окна задачи себе). Мутация `>=` → `>` здесь валила три
 * серверных проверки и лишь ОДНУ веб-проверку: два зеркала мутацию пережили,
 * потому что своё сравнение считали сами.
 *
 * ⚠️ ЧТО ИМЕННО РАСХОДИТСЯ МОЛЧА — включающий край. «Не раньше границы» — это
 * `>=`, а не `>`; правка края в этом модуле до двух рукописных `<` не доехала
 * бы вовсе, и разошлись бы они РОВНО НА ОДИН ДЕНЬ — на самой границе, то есть
 * в случае, который дневной прогон видит один раз из тридцати (Р-104) и один
 * из семи (Р-105). Сведение делает такой разъезд НЕВЫРАЗИМЫМ: край живёт в
 * одном `>=`, и другого сравнения даты с границей в проекте нет.
 *
 * Тот же приём, которым волна В9-c свела отказ перехода статуса к одной
 * функции (`statusTransitionDenial`): дешевле сделать расхождение
 * невозможным, чем ловить его проверкой.
 *
 * ⚠️ ФУНКЦИЯ ПРИНИМАЕТ ГРАНИЦУ, А НЕ «СЕГОДНЯ», и это не небрежность.
 * Оба веб-зеркала держат в руках уже посчитанную границу: одно кладёт её в
 * атрибут `min` поля даты, другое подставляет в текст отказа. Потребуй она
 * «сегодня» — зеркалам пришлось бы считать границу второй раз, и вернулась
 * бы ровно та вторая копия, ради снятия которой функция и заведена.
 */
export function isIsoDateAtOrAfterFloor(dateIso: string, floorIso: string): boolean {
  return dateIso >= floorIso;
}

// ─── Р-104 · час задним числом — не глубже начала ПРОШЛОГО месяца ─────────

/**
 * Нижняя граница даты факта: первое число ПРЕДЫДУЩЕГО календарного месяца.
 *
 * ⚠️ ГРАНИЦА КАЛЕНДАРНАЯ, А НЕ СКОЛЬЗЯЩАЯ, и это выбор владельца, а не
 * упрощение. «Текущий и прошлый месяц» означает, что 1-го числа окно резко
 * сужается до месяца с одним днём, а 31-го — почти до двух месяцев. Вариант
 * «неделя» владельцу предъявлялся и отвергнут с названной ценой: вернувшийся
 * из отпуска своих часов уже не внесёт.
 *
 * Январь считается без ветвлений по длине месяца: берётся только год и номер
 * месяца, число всегда `01`.
 */
export function factBackdateFloorIso(todayIso: string): string {
  const year = Number(todayIso.slice(0, 4));
  const month = Number(todayIso.slice(5, 7));
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  return `${String(prevYear).padStart(4, '0')}-${String(prevMonth).padStart(2, '0')}-01`;
}

/** Дата факта не глубже границы Р-104. `todayIso` — каноническое «сегодня». */
export function isFactDateWithinBackdateWindow(dateIso: string, todayIso: string): boolean {
  return isIsoDateAtOrAfterFloor(dateIso, factBackdateFloorIso(todayIso));
}

/**
 * Текст отказа Р-104 — ОДИН на сервер и на веб.
 *
 * ⚠️ ЧТО ДЕЛАТЬ НАЗВАНО ЧЕСТНО. «Обратитесь к руководителю» здесь было бы
 * ложным обещанием: запись факта строго своя (D04), чужие часы не пишет никто,
 * и обходного пути в интерфейсе НЕТ. Поэтому предлагается единственное, что
 * человек действительно может, — выбрать дату внутри окна.
 */
export function factBackdateRefusalMessage(floorIso: string): string {
  return `Часы можно записать не глубже ${humanIsoDate(floorIso)} — начала прошлого месяца. Выберите дату не раньше неё.`;
}

// ─── Р-105 · задача СЕБЕ — не раньше чем за 7 дней до сегодня ─────────────

/**
 * Ширина окна Р-105 в сутках.
 *
 * ⚠️ ОКНО ВКЛЮЧАЮЩЕЕ С ОБОИХ КОНЦОВ: ровно `today - 7` проходит. «Не раньше
 * чем за 7 дней до сегодня» — это `startDate >= today - 7`, а не `>`;
 * односторонняя реализация отличалась бы от правильной ровно один день в
 * неделю, и дневной прогон шесть раз из семи её бы не поймал.
 */
export const SELF_TASK_BACKDATE_DAYS = 7;

/** Нижняя граница `startDate` задачи, где постановщик = исполнитель (Р-105). */
export function selfTaskStartFloorIso(todayIso: string): string {
  return addDaysIso(todayIso, -SELF_TASK_BACKDATE_DAYS);
}

/** Начало задачи себе внутри окна Р-105. `todayIso` — каноническое «сегодня». */
export function isSelfTaskStartWithinWindow(startDateIso: string, todayIso: string): boolean {
  return isIsoDateAtOrAfterFloor(startDateIso, selfTaskStartFloorIso(todayIso));
}

/**
 * Текст отказа Р-105 — ОДИН на сервер и на веб.
 *
 * ⚠️ ТЕКСТ НАЗЫВАЕТ ИМЕННО «СЕБЕ», И ЭТО НЕ УКРАШЕНИЕ. Ограничение не
 * распространяется на задачу от руководителя сотруднику (граница 1 Р-21,
 * несущая и сохранённая Р-105); без слова «себе» человек прочитал бы отказ как
 * общий запрет и перестал бы оформлять задним числом то, что оформлять
 * по-прежнему можно.
 */
export function selfTaskBackdatedMessage(floorIso: string): string {
  return `Задачу себе можно начать не раньше ${humanIsoDate(floorIso)} — это неделя назад. Выберите дату не раньше неё.`;
}
